/**
 * ADR-0223 §3.3 — New idea (`/app/collab/ideas/new`): title (required, ≤200),
 * description, *Bring to a meeting* (a series select). The duplicate check
 * (§4.4) runs on blur of the title: a match shows an inline notice with
 * *Use this one* and *Create anyway*; the create repeats with confirm_new.
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { CollabPage, errBody, errMessage, type SeriesCard } from "../components/collab.js";

type Duplicate = { id: string; ref: string; title: string };

/** The blur checks, all on submit. Pure, for the tests. */
export function validateIdea(f: { title: string; description: string }): Partial<Record<"title" | "description", string>> {
  const errs: Partial<Record<"title" | "description", string>> = {};
  if (!f.title.trim()) errs.title = "A title is required.";
  else if (f.title.trim().length > 200) errs.title = "At most 200 characters.";
  if (f.description.length > 10000) errs.description = "At most 10000 characters.";
  return errs;
}

export function CollabIdeaCreate() {
  const navigate = useNavigate();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [seriesId, setSeriesId] = useState("");
  const [series, setSeries] = useState<SeriesCard[]>([]);
  const [errors, setErrors] = useState<Partial<Record<"title" | "description", string>>>({});
  const [duplicate, setDuplicate] = useState<Duplicate | null>(null);
  const [confirmNew, setConfirmNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<{ series: SeriesCard[] }>("/api/tenant/collab/series")
      .then((r) => setSeries(r.series))
      .catch(() => setSeries([]));
  }, []);

  async function checkDuplicate() {
    setErrors((e) => ({ ...e, title: validateIdea({ title, description }).title }));
    const t = title.trim();
    if (!t) return setDuplicate(null);
    try {
      const r = await apiFetch<{ duplicate: Duplicate | null }>(`/api/tenant/collab/ideas/duplicate?title=${encodeURIComponent(t)}`);
      setDuplicate(r.duplicate);
      if (!r.duplicate) setConfirmNew(false);
    } catch {
      setDuplicate(null);
    }
  }

  async function submit(force = false) {
    const errs = validateIdea({ title, description });
    setErrors(errs);
    if (Object.keys(errs).length > 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await apiFetch<{ idea: { id: string } }>("/api/tenant/collab/ideas", {
        method: "POST",
        body: { title: title.trim(), description: description.trim() || null, series_id: seriesId || null, confirm_new: force || confirmNew },
      });
      toast.push({ kind: "success", message: "Idea raised." });
      navigate(`/app/collab/ideas/${r.idea.id}`);
    } catch (e) {
      const b = errBody(e);
      if (b?.reason === "possible_duplicate" && b.duplicate) setDuplicate(b.duplicate);
      else setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <CollabPage title="New idea" noNewIdea crumb={<Link to="/app/collab/ideas">Ideas</Link>}>
      {error ? <ErrorState detail={error} /> : null}
      <div className="collabForm">
        <label className="collabLabel">
          Title
          <input className="collabInput" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} onBlur={() => void checkDuplicate()} placeholder="Anything worth doing that isn't an initiative, a study, a report or an issue yet" />
          {errors.title ? <span className="collabErr">{errors.title}</span> : null}
        </label>
        {duplicate ? (
          <div className="collabNotice" role="status">
            <span>
              Possible duplicate: {duplicate.ref} “{duplicate.title}” — add to its chatter instead?
            </span>
            <Link className="collabLinkBtn" to={`/app/collab/ideas/${duplicate.id}`}>
              Use this one
            </Link>
            <button
              type="button"
              className="collabLinkBtn"
              onClick={() => {
                setConfirmNew(true);
                setDuplicate(null);
              }}
            >
              Create anyway
            </button>
          </div>
        ) : null}
        <label className="collabLabel">
          Description
          <textarea className="collabInput" value={description} maxLength={10000} rows={5} onChange={(e) => setDescription(e.target.value)} onBlur={() => setErrors((e) => ({ ...e, description: validateIdea({ title, description }).description }))} />
          {errors.description ? <span className="collabErr">{errors.description}</span> : null}
        </label>
        <label className="collabLabel">
          Bring to a meeting
          <select className="collabInput" value={seriesId} onChange={(e) => setSeriesId(e.target.value)}>
            <option value="">Not yet</option>
            {series.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <span className="collabHelp">It gathers on that meeting's next agenda.</span>
        </label>
        <div className="collabPost" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="collabBtn collabBtnAmber" disabled={busy} onClick={() => void submit()}>
            Create
          </button>
          <Link className="collabBtn" to="/app/collab/ideas">
            Cancel
          </Link>
        </div>
      </div>
    </CollabPage>
  );
}
